import React, { useContext, useCallback } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { BingMapsView, BingMapsContextProvider } from '@bingads-webui-react/bing-maps';
import { Caption1Strong, Button, makeStyles, tokens, shorthands } from '@fluentui/react-components';
import MatchedLocationsTable from './matched-locations-table';
import { useBulkLocationsSearchContext } from './bulk-locations-search-provider';

const useStyles = makeStyles({
  header: {
    alignItems: 'center',
    display: 'flex',
    columnGap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalMNudge,
    marginBottom: tokens.spacingVerticalMNudge,
  },
  map: {
    height: '250px',
    width: '100%',
    marginTop: tokens.spacingVerticalMNudge,
  },
  zoomButtonContainer: {
    textAlign: 'right',
  },
  zoomButton: {
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorPrimary,
    paddingRight: tokens.spacingHorizontalNone,
    ...shorthands.borderRight('none'),
  },
});

const MatchedLocations = () => {
  const i18n = useContext(I18nContext);
  const {
    mapRef,
    matchedLocations,
    locationTargets,
    negativeLocationTargets,
    campaignApiToken,
    bingMapsApiKey,
    campaignApiServiceRoot,
    setMatchedLocations,
    mapId,
  } = useBulkLocationsSearchContext();
  const classes = useStyles();

  const handleUpdateLocations = useCallback((isTargeted) => {
    const updatedLocationTargets = matchedLocations.map(location => ({
      target: {
        ...location.target,
        isTargeted,
      },
    }));
    setMatchedLocations(updatedLocationTargets);
  }, [matchedLocations, setMatchedLocations]);

  const handleZoom = useCallback((zoomArgs) => {
    if (mapRef && mapRef.current) {
      mapRef.current.zoom(zoomArgs);
    }
  }, [mapRef]);

  return (
    <React.Fragment>
      <BingMapsContextProvider
        campaignApiToken={campaignApiToken}
        bingMapsApiKey={bingMapsApiKey}
        campaignApiServiceRoot={campaignApiServiceRoot}
      >
        <BingMapsView
          id={mapId}
          className={classes.map}
          locationTargets={locationTargets}
          negativeLocationTargets={negativeLocationTargets}
          ref={mapRef}
        />
        <div className={classes.zoomButtonContainer}>
          <Button
            appearance="transparent"
            onClick={handleZoom}
            className={classes.zoomButton}
          >
            {i18n.getString(_TL_('Zoom out to show all locations'))}
          </Button>
        </div>
      </BingMapsContextProvider>
      <div className={classes.header}>
        <Caption1Strong>{i18n.getString(_TL_('Found locations ({{count}})'), { count: matchedLocations.length })}</Caption1Strong>
        <Button onClick={() => handleUpdateLocations(true)} disabled={negativeLocationTargets.length === 0 && locationTargets.length > 0} size="small" className="target-all-button">{i18n.getString(_TL_('Target all'))}</Button>
        <Button onClick={() => handleUpdateLocations(false)} disabled={locationTargets.length === 0 && negativeLocationTargets.length > 0} size="small" className="exclude-all-button">{i18n.getString(_TL_('Exclude all'))}</Button>
      </div>
      <MatchedLocationsTable />
    </React.Fragment>
  );
};

export default MatchedLocations;
